import { ApiFailure } from 'common/types/ApiResponse';
import { useCallback, useReducer } from 'react';
import useTypedSelector from './useTypedSelector';

const BASE_URL = process.env.REACT_APP_API_BASE;

interface IUseApi<D> {
  loading: boolean;
  data: D | null;
  response: Response | null;
  error: string | null;
}

function init<D>(): IUseApi<D> {
  return {
    loading: false,
    data: null,
    response: null,
    error: null,
  };
}

type StartRequestAction = { type: 'request/start' };
type CompleteRequestAction<D> = {
  type: 'request/complete';
  payload: {
    data: D | undefined;
    response: Response | undefined;
    error: string | null | undefined;
  };
};
type UseApiAction<D> = StartRequestAction | CompleteRequestAction<D>;

function reducer<D>(state: IUseApi<D>, action: UseApiAction<D>): IUseApi<D> {
  switch (action.type) {
    case 'request/start': {
      return { ...state, loading: true };
    }
    case 'request/complete': {
      const {
        payload: { data, response, error },
      } = action;
      return {
        ...state,
        loading: false,
        data: data || state.data,
        response: response || state.response,
        error: error || null,
      };
    }
    default:
      return state;
  }
}

export type UseApiConfig = {
  url: string;
  method: 'POST' | 'PUT' | 'GET' | 'DELETE';
  token?: string;
  responseType?: 'json' | 'text/plain';
  contentType?: 'application/json' | 'application/x-www-form-urlencoded';
  baseUrl?: string;
  bypassTokenCheck?: boolean;
};

const baseConfig = {
  responseType: 'json',
  contentType: 'application/json',
  baseUrl: BASE_URL,
};

export type ApiResponse<D> = {
  res: Response | null;
  data: D | null;
  error: string | null;
};

type ApiPromise<D> = Promise<ApiResponse<D>>;

type UseApiReturn<D> = [
  IUseApi<D>,
  <B extends Record<string, unknown>>(
    body?: B,
    endpoint?: string
  ) => ApiPromise<D>
];

export function getCustomError(data?: ApiFailure) {
  if (data && data.payload.errorInfo) {
    if (data.payload.errorInfo.errorItems) {
      if (data.payload.errorInfo.errorItems.length === 1)
        return data.payload.errorInfo.errorItems[0];
      if (data.payload.errorInfo.errorItems.length >= 1)
        return data.payload.errorInfo.errorItems.reduce(
          (acc: string, m: string) => acc + '\n' + m,
          ''
        );
    } else if (data.payload.errorInfo.friendlyMessage) {
      return data.payload.errorInfo.friendlyMessage;
    }
  }
  return 'Unhandled error.';
}

export default function useApi<D = unknown>(
  config: UseApiConfig
): UseApiReturn<D> {
  const [state, dispatch] = useReducer(reducer, init<D>());
  const { access_token } = useTypedSelector((state) => state.auth);

  const {
    baseUrl,
    method,
    url: configUrl,
    token,
    responseType,
    contentType,
    bypassTokenCheck,
  } = { token: access_token, ...baseConfig, ...config };

  const request = useCallback(
    async <B extends Record<string, unknown>>(
      body: B = {} as B,
      endpoint: string | undefined = undefined
    ): ApiPromise<D> => {
      let error: string | null = null;
      let res: Response;
      if (token || bypassTokenCheck) {
        dispatch({ type: 'request/start' });
        try {
          const headers: RequestInit['headers'] = {
            'Content-Type': contentType || baseConfig.contentType,
          };
          if (token) {
            headers['Authorization'] = `Bearer ${token}`;
          }
          res = await fetch(`${baseUrl}${endpoint || configUrl}`, {
            body: getBody(
              method,
              contentType as UseApiConfig['contentType'],
              body as Record<string, string>
            ),
            method,
            headers,
          });
          const data =
            responseType === 'json' ? await res.json() : await res.text();
          if (data && data.payload && data.payload.responseCode === 'ERROR') {
            error = getCustomError(data);
          } else if (data && data.status >= 400) {
            if (data.message) {
              error = data.message;
            } else if (data.error) {
              // catch generic ResponseStatusException if
              error = data.error; // return default error
            }
          }

          dispatch({
            type: 'request/complete',
            payload: { data, response: res, error },
          });
          return { res, data, error };
        } catch (err) {
          if (typeof err === 'string') {
            error = err;
          } else if (err instanceof Error && err.message) {
            error = err.message;
          } else {
            error = 'Unhandled Error';
          }

          dispatch({
            type: 'request/complete',
            payload: { data: undefined, response: undefined, error: error },
          });
        }
      } else {
        console.warn(
          'No Token Found. If you like to make a request without token use bypassTokenCheck configuration option'
        );
      }
      return { res: null, data: null, error };
    },
    [
      contentType,
      token,
      baseUrl,
      configUrl,
      method,
      responseType,
      bypassTokenCheck,
    ]
  );

  return [state as IUseApi<D>, request];
}

function getBody(
  method: UseApiConfig['method'],
  contentType: UseApiConfig['contentType'],
  body: Record<string, string>
) {
  if (!body) {
    return undefined;
  }
  if (method === 'POST' || method === 'PUT') {
    if (contentType === 'application/x-www-form-urlencoded') {
      return new URLSearchParams(body);
    }
    return JSON.stringify(body);
  }

  return undefined;
}
